import React, { useEffect, useState } from "react";
import axios from "axios";
import { ShieldCheckIcon, LightningIcon, TargetIcon, WarningCircleIcon, ArrowClockwiseIcon, SquaresFourIcon } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_META = {
  conservative: { label: "Conservative", color: "#22D3EE", icon: ShieldCheckIcon, desc: "5-15% DD, 10-100 trades/mo, £5-20k profit" },
  balanced:     { label: "Balanced",     color: "#D6FF38", icon: TargetIcon,     desc: "40-300% growth, 3-25% DD, 3+ trades/mo" },
  aggressive:   { label: "Aggressive",   color: "#E879F9", icon: LightningIcon,  desc: ">300% growth, sub-40% DD" },
};

export default function Portfolios() {
  const [data, setData] = useState(null);
  const [diag, setDiag] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("conservative");

  const refresh = async () => {
    setLoading(true); setErr(null);
    try {
      const [pv, dg] = await Promise.all([
        axios.get(`${API}/admin/portfolio-preview`),
        axios.get(`${API}/admin/cycle-diagnosis?limit=100`),
      ]);
      setData(pv.data);
      setDiag(dg.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  if (loading) return <div className="p-8 mono text-sm text-[#94A3B8]" data-testid="portfolios-loading">loading portfolio preview…</div>;
  if (err) return <div className="p-8 mono text-sm text-[#FF4D4D]" data-testid="portfolios-error">{err}</div>;

  const tiers = data?.tiers || {};
  const totals = data?.totals || {};
  const activeTierBuckets = tiers[tab] || {};
  const bucketKeys = Object.keys(activeTierBuckets).sort();

  return (
    <div data-testid="portfolios-page" className="p-6 space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#22D3EE]">Admin · Store-worthy bots</div>
          <h1 className="text-3xl font-medium tracking-tight mt-2">Portfolio Preview</h1>
          <p className="text-sm text-[#94A3B8] mt-1 max-w-2xl">
            Everything currently viable for the store, grouped by tier × strategy × symbol. Refresh after each cycle to see what the brain produced.
          </p>
        </div>
        <button data-testid="refresh-btn" onClick={refresh}
                className="mono text-[10px] uppercase tracking-widest flex items-center gap-1.5 border border-[#2A2D3A] rounded-full px-3 py-2 text-[#F1F5F9] hover:border-[#D6FF38] transition-colors">
          <ArrowClockwiseIcon size={14} /> Refresh
        </button>
      </div>

      {/* Tier tabs */}
      <div className="flex flex-wrap gap-3">
        {(["conservative", "balanced", "aggressive"]).map((t) => {
          const M = TIER_META[t];
          const I = M.icon;
          const active = tab === t;
          const count = totals[t] || 0;
          return (
            <button key={t} onClick={() => setTab(t)}
                    data-testid={`tier-tab-${t}`}
                    className={`flex items-center gap-2 px-4 py-3 rounded-lg border transition-colors ${active ? "border-[#F1F5F9] bg-[#15171E]" : "border-[#2A2D3A] hover:border-[#94A3B8]"}`}
                    style={{ borderColor: active ? M.color : undefined }}>
              <I size={18} style={{ color: M.color }} />
              <div className="text-left">
                <div className="mono text-[10px] uppercase tracking-widest" style={{ color: M.color }}>{M.label}</div>
                <div className="mono text-xl">{count}</div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Cycle diagnosis top-bar */}
      {diag && diag.count > 0 && (
        <div className="border border-[#2A2D3A] bg-[#0F1116] rounded-lg p-4">
          <div className="flex items-center gap-2">
            <WarningCircleIcon size={16} className="text-[#FBBF24]" />
            <div className="mono text-[10px] uppercase tracking-widest text-[#FBBF24]">Cycle diagnosis · last {diag.count} synced bots</div>
          </div>
          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm">
            <div>
              <div className="mono text-[9px] uppercase tracking-widest text-[#64748B]">by status</div>
              <div className="mono text-xs text-[#F1F5F9]">
                {Object.entries(diag.by_status).map(([k, v]) => (
                  <span key={k} className="mr-3">{k}={v}</span>
                ))}
              </div>
            </div>
            <div>
              <div className="mono text-[9px] uppercase tracking-widest text-[#64748B]">by family</div>
              <div className="mono text-xs text-[#F1F5F9]">
                {Object.entries(diag.by_family).map(([k, v]) => (
                  <span key={k} className="mr-3">{k}={v}</span>
                ))}
              </div>
            </div>
            <div>
              <div className="mono text-[9px] uppercase tracking-widest text-[#64748B]">top failure reasons</div>
              <div className="mono text-xs text-[#F1F5F9]">
                {diag.top_failure_reasons.slice(0, 5).map(([r, c]) => (
                  <span key={r} className="mr-3">{r}({c})</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mono text-[10px] uppercase tracking-widest text-[#64748B]">{TIER_META[tab].desc}</div>

      {bucketKeys.length === 0 && (
        <div className="border border-dashed border-[#2A2D3A] bg-[#15171E]/50 rounded-lg p-8 text-center">
          <div className="mono text-sm text-[#94A3B8]">No {TIER_META[tab].label} bots yet.</div>
          <div className="mono text-[10px] text-[#64748B] mt-2">Once the brain produces one that hits the tier&apos;s profile, it&apos;ll appear here automatically.</div>
        </div>
      )}

      {bucketKeys.map((bucket) => {
        const rows = activeTierBuckets[bucket];
        return (
          <div key={bucket} data-testid={`bucket-${bucket.replace(/\s|·/g, "-")}`}
               className="border border-[#2A2D3A] bg-[#15171E] rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-[#2A2D3A] bg-[#0F1116] flex items-center justify-between">
              <div>
                <div className="mono text-sm text-[#F1F5F9]">{bucket}</div>
                <div className="mono text-[9px] uppercase tracking-widest text-[#64748B]">{rows.length} bot(s)</div>
              </div>
              <SquaresFourIcon size={16} className="text-[#22D3EE]" />
            </div>
            <div className="divide-y divide-[#2A2D3A]">
              {rows.map((r) => (
                <div key={r.id} className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="mono text-sm text-[#F1F5F9] truncate">{r.name}</div>
                    <div className="mono text-[10px] text-[#64748B] mt-0.5 truncate">
                      {r.raw_name !== r.name ? r.raw_name : r.symbol}
                    </div>
                  </div>
                  <div className="mono text-[10px] text-right">
                    <div className="text-[#D6FF38]">+{r.growth_pct}% · <span className="text-[#FF4D4D]">-{r.max_drawdown}% DD</span></div>
                    <div className="text-[#64748B]">{r.total_trades} trades · {r.months_tested} months</div>
                  </div>
                  <button
                    onClick={() => {
                      try {
                        if (navigator?.clipboard?.writeText) {
                          navigator.clipboard.writeText(r.raw_name).catch(() => {});
                        }
                      } catch { /* clipboard unavailable */ }
                    }}
                    data-testid={`copy-${r.id}`}
                    className="mono text-[9px] uppercase tracking-widest px-2 py-1 rounded border border-[#2A2D3A] text-[#D6FF38] hover:border-[#D6FF38] transition-colors">
                    Copy ID
                  </button>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
